import { v4 as uuidv4 } from 'uuid';
import { parse } from 'csv-parse/sync';
import * as turf from '@turf/turf';
import { Polygon, MultiPolygon } from 'geojson';
import { getPointFromAddress } from '@/api/location';
import { ScoutLocation } from '@/types/location';
import {
  GeoJSONFeatureCollection,
  GeoJSONMultiPolygon,
  GeoJSONPolygon,
  GeoJSONType,
} from '@/types/geojson';
import { convertPointToPolygon, standardizeType } from '@/utils/scout';
import { validate } from '@/utils/validation';
import { getSegmentsFromPolygon } from '@/api/segments';

export const getLocationName = (location: Record<string, string>) => {
  try {
    if (location.name) {
      return location.name;
    }

    if (location.type.toLowerCase() === GeoJSONType.Address.toLowerCase()) {
      return location.location;
    }

    return location.type || 'Location Name Missing';
  } catch (error) {
    return 'Location Name Missing';
  }
};

export const getPopulatedProperties = (location: Record<string, string>) => {
  const populatedProperties = [];
  for (let [key, value] of Object.entries(location)) {
    if (key.trim() === '' && value.trim() === '') {
      continue;
    }

    if (key.trim() === '' || value.trim() === '') {
      key = key || value;
      value = value || key;
    }

    populatedProperties.push({ key, value });
  }

  return populatedProperties;
};

export const inferType = (value: string) => {
  const regex = /^-?\d+\.\d+,\s*-?\d+\.\d+$/;

  if (regex.test(value)) {
    return GeoJSONType.Point;
  }

  return GeoJSONType.Address;
};

export const processFile = (
  file: File,
  type: string,
  onSuccess: (locations: ScoutLocation[], file: File) => void,
  onFailure: (errorMessage: string) => void,
) => {
  const reader = new FileReader();
  reader.onload = async e => {
    const content = e.target?.result as string;

    if (type === 'csv') {
      try {
        const locations = parse(content, {
          columns: true,
          skip_empty_lines: true,
          trim: true,
        });

        if (locations.length < 1) {
          throw new Error('No locations found in CSV');
        }

        const parsedCsv = [];
        for (const location of locations) {
          const properties = getPopulatedProperties(location);

          if (properties.length < 1) {
            throw new Error('No valid properties found in CSV');
          }

          if (properties.length === 1) {
            location.type = inferType(properties[0].value);
            location.location = properties[0].value;
          }

          const polygon = await geojsonBasedOnType(location);

          if (!polygon) {
            throw new Error('Error creating polygon from location');
          }

          let geojson = polygon;
          let segments;

          const filters =
            location.type === GeoJSONType.Address ||
            location.type === GeoJSONType.Point
              ? []
              : ['parkingAisle', 'driveway'];
          const { segmentsGeometry } = await getSegmentsFromPolygon(
            polygon,
            filters,
          );

          if (segmentsGeometry) {
            // create a 20 meter buffer around the merged lines to represent the location
            const feature = turf.buffer(segmentsGeometry, 20, {
              units: 'meters',
            });

            if (feature) {
              geojson = feature.geometry;
              segments = segmentsGeometry;
            }
          }

          if (geojson) {
            parsedCsv.push({
              _id: uuidv4(),
              name: getLocationName(location),
              geojson,
              segments,
              description: location.description,
            });
          }
        }

        const validCsv = validate(parsedCsv, 'csv') as ScoutLocation[];
        onSuccess(validCsv, file);
      } catch (error) {
        console.error('Error parsing CSV:', error);
        onFailure(error as string);
      }
    } else if (type === 'geojson') {
      try {
        const validGeoJson = validate(content, 'geojson');
        const parsedGeojson = await parseGeojsonToLocations(
          validGeoJson as GeoJSONFeatureCollection,
        );

        onSuccess(parsedGeojson, file);
      } catch (error) {
        console.error('Error parsing GeoJSON:', error);
        onFailure(error as string);
      }
    } else {
      onFailure('Unsupported file type');
    }
  };

  reader.readAsText(file);
};

export const parseGeojsonToLocations = async (
  geojson: GeoJSONFeatureCollection,
) => {
  const locations = [];
  for (const feature of geojson.features) {
    let geojson = feature.geometry as GeoJSONPolygon | GeoJSONMultiPolygon;
    let segments;

    const { segmentsGeometry } = await getSegmentsFromPolygon(geojson, [
      'parkingAisle',
      'driveway',
    ]);

    if (segmentsGeometry) {
      // create a 20 meter buffer around the merged lines to represent the location
      const newFeature = turf.buffer(segmentsGeometry, 20, {
        units: 'meters',
      });

      if (newFeature) {
        geojson = newFeature.geometry as GeoJSONPolygon | GeoJSONMultiPolygon;
        segments = segmentsGeometry;
      }
    }

    const location: ScoutLocation = {
      _id: uuidv4(),
      geojson: geojson,
      segments,
      name: feature.properties?.name || feature.type,
      description: feature.properties?.description,
    };
    locations.push(location);
  }

  return locations;
};

export const parseJsonToLocations = (
  json: ScoutLocation[],
): ScoutLocation[] => {
  const locations = json.map(location => ({
    ...location,
    _id: uuidv4(),
  }));

  return locations;
};

export function downloadJson(url: string, filename: string) {
  fetch(url)
    .then(response => response.json())
    .then(json => {
      const blob = new Blob([JSON.stringify(json)], {
        type: 'application/json',
      });
      const href = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = href;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    })
    .catch(console.error);
}

export const geojsonBasedOnType = async (location: Record<string, string>) => {
  location.type = standardizeType(location.type);

  try {
    if (location.type === GeoJSONType.Address) {
      const response = await getPointFromAddress(location.location);

      if ('error' in response || !response.features) {
        throw new Error(
          'Error getting point from address: ' + location.location,
        );
      }

      const { center } = response.features[0];
      if (!center) {
        throw new Error(
          'Error getting center from address: ' + location.location,
        );
      }

      const coords = center.join(', ');
      return convertPointToPolygon(coords);
    } else if (location.type === GeoJSONType.Point) {
      return convertPointToPolygon(location.location, true);
    } else if (
      location.type === GeoJSONType.Polygon ||
      location.type === GeoJSONType.MultiPolygon
    ) {
      return {
        type: location.type,
        coordinates: JSON.parse(
          '[' + location.location.replace(/, /g, ',') + ']',
        ),
      } as Polygon | MultiPolygon;
    } else {
      return null;
    }
  } catch (error) {
    return null;
  }
};
